"use client";

import Image from "next/image";
import Link from "next/link";
import type { ReactElement, ReactNode } from "react";
import { Facebook, Instagram, Linkedin, SquareArrowOutUpRight, Twitter } from "lucide-react";
import { useLocale } from "@/app/[lang]/providers/LocaleProvider";
import { useDict } from "@/app/[lang]/providers/DictionaryProvider";
import { useFooter } from "@/hooks/useFooter";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { LocalizedLink } from "@/components/common/LocalizedLink";

const SOCIAL_ICON_BACKGROUND =
  'linear-gradient(110deg, #D9E2FF 0%, #B9D6FE 50%, #9ECCFD 100%)';

function SocialLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="group"
    >
      <span
        className="flex items-center justify-center w-9 h-9 rounded-full text-primary-700 transition-transform duration-200 group-hover:scale-105"
        style={{ background: SOCIAL_ICON_BACKGROUND }}
      >
        {children}
      </span>
    </a>
  );
}

export default function Footer() {
  const lang = useLocale();
  const dict = useDict();
  const { data: footerData } = useFooter(lang as 'en' | 'id');
  const { data: siteSettings } = useSiteSettings();

  const socialLinks = [
    { href: siteSettings?.data?.facebook_url, label: 'Facebook', icon: <Facebook size={18} aria-hidden /> },
    { href: siteSettings?.data?.instagram_url, label: 'Instagram', icon: <Instagram size={18} aria-hidden /> },
    { href: siteSettings?.data?.twitter_url, label: 'Twitter', icon: <Twitter size={18} aria-hidden /> },
    { href: siteSettings?.data?.linkedin_url, label: 'LinkedIn', icon: <Linkedin size={18} aria-hidden /> },
  ].filter(
    (s): s is { href: string; label: string; icon: ReactElement } =>
      typeof s.href === 'string' && s.href.trim().length > 0
  );
  return (
    <footer className="bg-primary-800 text-white py-10">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-10">
        {/* Kiri */}
        <div className="space-y-4">
          <Image
            src="/images/logo-white.png"
            alt="SMF Logo"
            width={150}
            height={50}
            style={{
              "height": "auto"
            }}
            className="mb-3 w-37.5 h-auto"
          />
          <p className="text-base font-semibold mb-3">PT Sarana Multigriya Finansial (Persero)</p>
          <div className="flex items-start gap-2 text-base">
            <svg className="w-4 h-4 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
            <span>Grha SMF<br />Jl. Panglima Polim I No 1 Melawai<br />Kebayoran Baru Jakarta 12160</span>
          </div>
          <div className="flex items-start gap-2 text-base mt-2">
            <svg className="w-4 h-4 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.61 21 3 13.39 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.58a1 1 0 01-.25 1.01l-2.2 2.2z"/></svg>
            <span>+ 62 21 2700400<br />+ 62 21 2701400 (Fax)</span>
          </div>
          <div className="flex items-start gap-2 text-base mt-2">
            <svg className="w-4 h-4 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
            <span>info@smf-indonesia.co.id<br /><br /><br />{dict.footer.ojk_notice_id}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-1 gap-2 text-base">
          {footerData?.data?.map((item) => (
            <div key={item.id}>
              <LocalizedLink href={item.url} className="hover:text-[#5C51E0] font-semibold">
                {item.title}
              </LocalizedLink>
              {item.children && item.children.length > 0 && (
                <ul className="mt-1 space-y-1 pl-2">
                  {item.children.map((child) => (
                    <li key={child.id}>
                      <LocalizedLink href={child.url} className="hover:text-[#5C51E0]">
                        {child.title}
                      </LocalizedLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-col justify-start items-center md:items-end gap-8">
          <Link
            href="https://research.smf-indonesia.co.id"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 font-semibold hover:text-[#5C51E0]"
          >
            SMF Research Institute
            <SquareArrowOutUpRight size={18} aria-hidden />
          </Link>

          {/* Social media — URL dari API /api/site-settings */}
          {socialLinks.length > 0 && (
            <div className="flex flex-col items-center md:items-end gap-3 mt-8">
              <div className="flex items-center gap-3">
                {socialLinks.map((social) => (
                  <SocialLink key={social.label} href={social.href} label={social.label}>
                    {social.icon}
                  </SocialLink>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
